// H.R.M.E.Z screen help (the "?" next to the title; it covers the capture pad, Memory and
// Folders & Rules). Plain words; keep it in step with apps/mobile/src/tools/HrmezTool.tsx.
import type { ScreenHelpContent } from '../../v10/ScreenHelp';

export const hrmezHelp: ScreenHelpContent = {
  title: 'H.R.M.E.Z',
  intro:
    "H.R.M.E.Z is a quick-capture pad. When a thought hits, type it here before you lose it, then send it to the right place: a note, a task, a calendar event, or your journal. It also keeps your folders and file-sorting rules.",
  sections: [
    {
      heading: 'Capture a thought',
      body: [
        'Type in the big box ("Capture a thought…"). It saves a draft by itself a moment after you stop typing, so nothing is lost if you leave.',
        'The first line of what you write becomes the title of whatever it turns into.',
      ],
    },
    {
      heading: 'Pick where it goes',
      body: [
        'The round icons above the box are destinations. Tap one to pick it (tap again to unpick it). The line under them says where it will go, for example "Routing to: List".',
        'Press and hold an icon to see its options. For Notes you can pick a note folder. For List you can pick a task list. Tap one to choose it, and it shows after a ›. For Discard it tells you how long before it is cleared.',
      ],
      items: [
        { term: 'N  Notes', text: 'makes a new note.' },
        { term: 'L  List', text: 'makes a new task in the list you picked, or your oldest list if you did not pick one.' },
        { term: 'J  Clio Journal Entry', text: 'adds a journal entry for today, using your "PM" journal entry type. If you do not have one, a warning tells you.' },
        { term: 'D  Discard', text: 'throws it away. It is cleared after 48 hours.' },
        { term: 'Cal  Calendar', text: 'adds an event today from 09:00 to 09:30, with your text as its notes.' },
      ],
    },
    {
      heading: 'No icon picked',
      body: [
        '"No icon selected" means HRMEZ decides for you when you send. At the moment it files the thought as a note.',
      ],
    },
    {
      heading: 'The buttons',
      items: [
        { term: '✓ Send', text: 'sends your text to the place you picked and clears the box. It is greyed out while the box is empty.' },
        { term: '✕', text: 'throws the draft away. It asks "Discard this entry?" first.' },
        { term: '▼ Recents', text: 'opens your last entries. Each shows its icon, its first line, where it went and the date. The ✕ on a row deletes that entry.' },
      ],
    },
    {
      heading: 'Memory',
      body: [
        'The Memory button opens "AI Memory": a read-only log of the file moves HRMEZ has made, with where each file came from, where it went, why, and whether a rule, the AI or you decided. It shows 50 at a time; use Previous and Next. Close shuts it.',
      ],
    },
    {
      heading: 'Folders & Rules',
      body: ['This button opens a panel for how HRMEZ files things.'],
      items: [
        { term: 'Google Drive Connection', text: 'shows whether Drive is connected. Press Connect to link it. Once connected, Run Now sorts your Drive files and tells you how many it processed, moved and flagged.' },
        { term: 'Local Folders', text: 'your own folder tree. It starts with Social, Health, Admin, Personal and Economic, each holding Projects, Areas, Resources, Tasks and Systems. Tap a folder to open it. The trail at the top takes you back up. Add a folder or subfolder at the bottom, Edit renames, Delete removes it and everything inside it (it asks first).' },
        { term: 'Files', text: 'inside a folder you can + Upload files, tap one to open it, or Delete it.' },
        { term: 'Sorting Rules', text: 'type part of a filename and a destination path, then Add Rule. A file whose name contains that text goes to that path. Delete removes a rule.' },
      ],
    },
    {
      heading: 'What saves',
      body: [
        'Your draft saves as you type. Send, Discard, Add, Edit, Delete, Upload and Add Rule take effect right away. There is no Save button.',
        'The icons themselves (their letters, colours and options) are edited in Settings, not here.',
      ],
    },
  ],
};
